import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import enhanceWithClickOutside from 'react-click-outside';
import MediaQuery from 'react-responsive';
import { autobind } from 'core-decorators';
import IndexLink from 'react-router/lib/IndexLink';
import Link from 'react-router/lib/Link';
import CSSModules from 'react-css-modules';
import classNames from 'classnames';
import get from 'lodash/get';

import { actions as joinPopupActions } from '../../redux/communities/joinPopup';
import { actions } from '../../redux/ui/overlay';
import { getNavConfig, TITLE } from '../../constants';
import { ROOT, LOGIN } from '../../constants/routes';
import { BREAKPOINT, COLORS } from '../../constants/styles';
import {
  isLoggedInSelector,
  selectRegistrationCompleted,
  selectRequestPasswordUpdate,
} from '../../redux/auth/selectors';
import {
  loggedUserSelector,
  hasProviderProfileSelector,
  selectLoggedProviderTeamsList,
  selectIsTeamMemberProvider,
  selectUserToShow,
  selectDashboardVisible,
} from '../../redux/context/selectors';
import AppNotificationsBtn from './Components/AppNotificationsBtn';
import Button from '../Button';
import Icon from '../Icon';
import Menu from './Menu';
import styles from './index.styl';
import brandConfig from '../../constants/brandConfig';
import { isSupportedBrowser } from '../../utils/browserChecker';

/*
 * TODO:
 * - avatar layout changing according to screen width
 * Now it's implemented according to components state
 * - header overlay z-indexes to resolve!
 */

const IS_BROWSER_SUPPORTABLE = isSupportedBrowser();

const mapStateToProps = state => ({
  isLoggedIn: isLoggedInSelector(state),
  loggedUser: loggedUserSelector(state),
  user: selectUserToShow(state),
  logoutSucceed: state.auth.logout.succeed,
  signupSending: state.auth.signup.sending,
  isProvider: hasProviderProfileSelector(state),
  teamsList: selectLoggedProviderTeamsList(state),
  isTeamMemberProvider: selectIsTeamMemberProvider(state),
  dashboardVisible: selectDashboardVisible(state),
  isRequestPasswordUpdate: selectRequestPasswordUpdate(state),
  isRegistrationCompleted: selectRegistrationCompleted(state),
});

const mapDispatchToProps = {
  hideOverlay: actions.hide,
  toggleOverlay: actions.toggleVisibility,
  openJoinPopup: joinPopupActions.show,
};

@injectIntl
@withRouter
@connect(
  mapStateToProps,
  mapDispatchToProps,
)
@enhanceWithClickOutside
@CSSModules(styles, { allowMultiple: true })
export default class Header extends PureComponent {
  static propTypes = {
    hideOverlay: PropTypes.func.isRequired,
    isLoggedIn: PropTypes.bool,
    loggedUser: PropTypes.object,
    toggleOverlay: PropTypes.func.isRequired,
    logoutSucceed: PropTypes.bool,
    signupSending: PropTypes.bool,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    openJoinPopup: PropTypes.func,
    isProvider: PropTypes.bool,
    teamsList: PropTypes.array,
    dashboardVisible: PropTypes.bool,
    isTeamMemberProvider: PropTypes.bool,
    user: PropTypes.object,
    isRequestPasswordUpdate: PropTypes.bool,
    isRegistrationCompleted: PropTypes.bool,
  };

  static defaultProps = {
    isLoggedIn: null,
    loggedUser: null,
    logoutSucceed: false,
    signupSending: false,
    openJoinPopup: false,
    isProvider: false,
  };

  state = {
    isOpened: false,
    loggedOut: false,
  };

  componentWillReceiveProps(nextProps) {
    const { logoutSucceed, signupSending } = this.props;
    const logoutUpdated = logoutSucceed !== nextProps.logoutSucceed;

    if (logoutUpdated) {
      // prevent redirecting on autologout when accept invitation from loggedIn state
      const signupInProgress = !signupSending && nextProps.signupSending;

      this.setState({
        loggedOut: nextProps.logoutSucceed && !signupInProgress,
      });
    }
  }

  @autobind
  handleClickOutside() {
    this.closeMobileHeader();
  }

  @autobind
  toggleMobileHeader() {
    const { isOpened } = this.state;
    const { toggleOverlay } = this.props;
    this.setState({ isOpened: !isOpened });
    toggleOverlay();
  }

  @autobind
  closeMobileHeader() {
    const { isOpened } = this.state;
    const { hideOverlay } = this.props;
    if (isOpened) {
      this.setState({ isOpened: !isOpened });
      hideOverlay();
    }
  }

  getTeamsMenuItems = () => {
    const { loggedUser, teamsList } = this.props;
    const loggedUserItem = {
      logo: get(loggedUser, 'avatar'),
      name: get(loggedUser, 'displayableName'),
      id: get(loggedUser, 'id'),
      isLoggedUser: true,
    };
    return [loggedUserItem, ...teamsList];
  };

  render() {
    const { isOpened, loggedOut } = this.state;
    const {
      location,
      isLoggedIn,
      user,
      isProvider,
      intl: { formatMessage },
      openJoinPopup,
      isTeamMemberProvider,
      dashboardVisible,
      loggedUser,
      isRequestPasswordUpdate,
      isRegistrationCompleted,
    } = this.props;

    const loggedIn =
      isLoggedIn && !isRequestPasswordUpdate && isRegistrationCompleted;

    // avoid infinite loop on logout
    if (loggedOut && location.pathname !== LOGIN) {
      // browserHistory.push(LOGIN);
      return null;
    }
    const { id: loggedUserId } = loggedUser;

    const currentActive = location.pathname.slice(1);
    const isCommunityPreview = currentActive.includes('community-preview');

    return (
      <header
        styleName={classNames('root', {
          'root--opened-mobile': isOpened,
        })}
        /* eslint-disable-next-line react/jsx-handler-names */
        onClick={this.closeMobileHeader}
      >
        <MediaQuery maxWidth={BREAKPOINT}>
          {isMobile => {
            if (!isMobile && isOpened) this.closeMobileHeader();
            return null;
          }}
        </MediaQuery>
        <div styleName="item">
          <IndexLink styleName="link home-link" to={ROOT} aria-label="home">
            <Icon name={brandConfig.logo} size={brandConfig.logoSize} />
          </IndexLink>
        </div>

        {IS_BROWSER_SUPPORTABLE && (
          <Fragment>
            <div styleName="item item--title">
              <Link styleName="link" to={ROOT}>
                <FormattedMessage {...TITLE} />
              </Link>
            </div>
            <Menu
              {...{ isOpened, user, isProvider }}
              items={getNavConfig(
                formatMessage,
                dashboardVisible,
                loggedUserId,
                isProvider,
              )}
              isLoggedIn={loggedIn}
              isCommunityPreview={isCommunityPreview}
              openJoinPopup={openJoinPopup}
              teamsMenuItems={this.getTeamsMenuItems()}
              isTeamMemberProvider={isTeamMemberProvider}
              loggedUserId={loggedUserId}
            />
            <div className="g-shownOnMobile" styleName="separator" />
            <If condition={loggedIn}>
              <MediaQuery maxWidth={BREAKPOINT}>
                <AppNotificationsBtn isProvider={isProvider} />
              </MediaQuery>
            </If>
            <If condition={loggedIn || isCommunityPreview}>
              <div styleName={classNames('item item--menu')} className="ph2">
                <Button
                  icon
                  className={classNames({ dn: !isOpened })}
                  onClick={() => this.toggleMobileHeader()}
                >
                  <Icon name="close" style={{ stroke: COLORS.bluePrimary }} />
                </Button>
                <Button
                  icon
                  className={classNames({ dn: isOpened })}
                  onClick={() => this.toggleMobileHeader()}
                >
                  <Icon name="menu" style={{ stroke: COLORS.bluePrimary }} />
                </Button>
              </div>
            </If>
          </Fragment>
        )}
      </header>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Header/index.jsx